// apps/patient/src/services/requestQueue.js
// Request queue with concurrency limiting and retry logic

class RequestQueue {
  constructor(options = {}) {
    this.maxConcurrent = options.maxConcurrent || 4;
    this.retryAttempts = options.retryAttempts || 3;
    this.retryDelay = options.retryDelay || 1000; // Base delay in ms
    this.retryStatusCodes = options.retryStatusCodes || [502, 503, 504, 429];
    
    this.activeRequests = 0;
    this.queue = [];
    this.paused = false;
    
    // Debug mode - set to true to see queue activity
    this.debug = options.debug || false;
  }

  log(...args) {
    if (this.debug) {
    }
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Async function that performs the fetch
   * @param {Object} options - Request options
   * @param {number} options.priority - Higher priority requests are processed first (default: 0)
   * @param {string} options.label - Optional label for debugging
   * @returns {Promise} - Resolves with the request result
   */
  enqueue(requestFn, options = {}) {
    const { priority = 0, label = 'request' } = options;

    return new Promise((resolve, reject) => {
      const queueItem = {
        requestFn,
        resolve,
        reject,
        priority,
        label,
        addedAt: Date.now()
      };

      // Insert based on priority (higher priority first)
      const insertIndex = this.queue.findIndex(item => item.priority < priority);
      if (insertIndex === -1) {
        this.queue.push(queueItem);
      } else {
        this.queue.splice(insertIndex, 0, queueItem);
      }

      this.log(`Enqueued: ${label} (priority: ${priority}, queue length: ${this.queue.length})`);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.paused) {
      this.log('Queue is paused');
      return;
    }

    if (this.activeRequests >= this.maxConcurrent) {
      this.log(`At max concurrency (${this.activeRequests}/${this.maxConcurrent})`);
      return;
    }

    if (this.queue.length === 0) {
      this.log('Queue is empty');
      return;
    }

    const item = this.queue.shift();
    this.activeRequests++;
    
    this.log(`Processing: ${item.label} (active: ${this.activeRequests}, queued: ${this.queue.length})`);

    try {
      const result = await this.executeWithRetry(item);
      item.resolve(result);
    } catch (error) {
      item.reject(error);
    } finally {
      this.activeRequests--;
      this.log(`Completed: ${item.label} (active: ${this.activeRequests}, queued: ${this.queue.length})`);
      
      // Process next item in queue
      this.processQueue();
    }
  }

  async executeWithRetry(item, attempt = 1) {
    try {
      return await item.requestFn();
    } catch (error) {
      const shouldRetry = this.shouldRetry(error, attempt);
      
      if (shouldRetry) {
        const delay = this.calculateDelay(attempt);
        this.log(`Retry ${attempt}/${this.retryAttempts} for ${item.label} after ${delay}ms`);
        
        await this.sleep(delay);
        return this.executeWithRetry(item, attempt + 1);
      }
      
      throw error;
    }
  }

  shouldRetry(error, attempt) {
    if (attempt >= this.retryAttempts) {
      return false;
    }

    // Retry on network errors
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      return true;
    }

    // Retry on specific status codes
    if (error.status && this.retryStatusCodes.includes(error.status)) {
      return true;
    }

    // Check if error message contains status code indicators
    const errorMsg = error.message || '';
    if (this.retryStatusCodes.some(code => errorMsg.includes(code.toString()))) {
      return true;
    }

    return false;
  }

  calculateDelay(attempt) {
    // Exponential backoff with jitter
    const exponentialDelay = this.retryDelay * Math.pow(2, attempt - 1);
    const jitter = Math.random() * 500; // 0-500ms jitter
    return Math.min(exponentialDelay + jitter, 10000); // Cap at 10 seconds
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Pause processing (doesn't affect active requests)
   */
  pause() {
    this.paused = true;
    this.log('Queue paused');
  }

  /**
   * Resume processing
   */
  resume() {
    this.paused = false;
    this.log('Queue resumed');
    this.processQueue();
  }

  /**
   * Clear all pending requests
   * @param {Error} error - Optional error to reject pending requests with
   */
  clear(error = new Error('Queue cleared')) {
    const pending = this.queue.length;
    this.queue.forEach(item => item.reject(error));
    this.queue = [];
    this.log(`Cleared ${pending} pending requests`);
  }

  /**
   * Get queue statistics
   */
  getStats() {
    return {
      active: this.activeRequests,
      queued: this.queue.length,
      maxConcurrent: this.maxConcurrent,
      paused: this.paused
    };
  }
}

// Create singleton instance with default settings
const requestQueue = new RequestQueue({
  maxConcurrent: 4,
  retryAttempts: 3,
  retryDelay: 1000,
  debug: import.meta.env.DEV // Debug in development
});

// Priority levels for common request types
export const PRIORITY = {
  CRITICAL: 100,    // Auth, user profile
  HIGH: 75,         // Feature flags, permissions
  NORMAL: 50,       // Page data
  LOW: 25,          // Analytics, background sync
  BACKGROUND: 0     // Prefetching, non-essential
};

export { RequestQueue, requestQueue };
export default requestQueue;